'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ColumnPinningState, OnChangeFn, Updater } from '@tanstack/react-table';
import type { TableColumnDef } from './column';

export type TableColumnPinning<T extends object> = Record<keyof T, boolean>;
export type TableOnColumnPinningChange<T extends object> = (state: TableColumnPinning<T>) => void;

function getInitialColumnPinning<TData extends object, TValue>(
  columns: TableColumnDef<TData, TValue>[],
  controlledColumnPinning: TableColumnPinning<TData> | undefined,
): ColumnPinningState {
  const left: string[] = [];
  const right: string[] = [];

  for (const column of columns) {
    if (controlledColumnPinning?.[column.id as keyof TData] || column.pin) {
      left.push(column.id);
    } else {
      break;
    }
  }

  const reversedColumns = [...columns].reverse();
  for (const column of reversedColumns) {
    if ((controlledColumnPinning?.[column.id as keyof TData] || column.pin) && !left.includes(column.id)) {
      right.push(column.id);
    } else {
      break;
    }
  }

  return { left, right };
}

export function useColumnPinning<TData extends object, TValue>(
  columns: TableColumnDef<TData, TValue>[],
  controlledColumnPinning: TableColumnPinning<TData> | undefined,
  onColumnPin: TableOnColumnPinningChange<TData> | undefined,
): [ColumnPinningState, OnChangeFn<ColumnPinningState>] {
  const hasUserUpdatedRef = useRef(false);
  const [state, setState] = useState<ColumnPinningState>(getInitialColumnPinning(columns, controlledColumnPinning));
  const handleColumnPinningChange: OnChangeFn<ColumnPinningState> = useCallback(
    (updater: Updater<ColumnPinningState>) => {
      const result = typeof updater === 'function' ? updater(state) : updater;
      setState(result);

      hasUserUpdatedRef.current = true;

      if (onColumnPin) {
        onColumnPin(result as TableColumnPinning<TData>);
      }

      return result;
    },
    [state, onColumnPin],
  );

  useEffect(() => {
    if (!hasUserUpdatedRef.current) {
      setState(getInitialColumnPinning(columns, controlledColumnPinning));
    }
  }, [columns, controlledColumnPinning]);

  return useMemo(() => [state, handleColumnPinningChange], [state, handleColumnPinningChange]);
}
